/**
 * Target zones (design §5.1, §6). Keys pick the zone, the look picks the
 * opponent and the line. Each limb has a natural target, and ALT gives it
 * the other upper one:
 *
 * |            | nothing | ALT (F) | LOW (Shift)               |
 * | ---------- | ------- | ------- | ------------------------- |
 * | Arm        | head    | body    | body (a fist goes no lower) |
 * | Leg        | body    | head    | legs                      |
 * | Cover      | head    | body    | legs: the lead shin checks |
 *
 * LOW wins when both are held.
 */
import { Buttons } from "./input.ts";
import type { Limb, Zone } from "./state.ts";
import { tuning } from "./tuning.ts";

export const ZONE_NAMES = ["head", "body", "legs"] as const;

/** The zone a strike goes for, from the modifiers held at the press. */
export function strikeZone(limb: Limb, buttons: number): Zone {
  const low = (buttons & Buttons.AIM_LOW) !== 0;
  const alt = (buttons & Buttons.AIM_ALT) !== 0;
  if (limb < 2) return low || alt ? 1 : 0;
  return low ? 2 : alt ? 0 : 1;
}

/** The zone a cover holds, from the modifiers held with it. */
export function coverZone(buttons: number): Zone {
  if ((buttons & Buttons.AIM_LOW) !== 0) return 2;
  return (buttons & Buttons.AIM_ALT) !== 0 ? 1 : 0;
}

/** Modifiers a limb press needs to go for `zone` (bots, tools, tests). An arm asked for the legs goes to the body. */
export function aimButtons(limb: Limb, zone: Zone): number {
  if (limb < 2) return zone === 0 ? 0 : Buttons.AIM_ALT;
  return zone === 2 ? Buttons.AIM_LOW : zone === 0 ? Buttons.AIM_ALT : 0;
}

/** Modifiers a cover needs to hold `zone`. */
export function coverButtons(zone: Zone): number {
  return zone === 2 ? Buttons.AIM_LOW : zone === 1 ? Buttons.AIM_ALT : 0;
}

/** Height above the floor a strike at `zone` goes for. */
export function zoneHeight(zone: Zone): number {
  const z = tuning.zones;
  return zone === 0 ? z.head : zone === 1 ? z.body : z.legs;
}
